import { BookOpen, Check, Download, Flag, History, MapPin, Pencil, Play, Plus, Search, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import { Avatar } from '../../components/Avatar.tsx';
import type { RoundInvitation } from '../../lib/rounds.ts';
import { listRoundInvitations } from '../../lib/rounds.ts';
import { useAuth } from '../../lib/auth.tsx';
import type { Course } from '../../lib/courses.ts';
import { currentPosition, deleteCourse, exportCourse, listCourses, telechargerDocument } from '../../lib/courses.ts';

/* La liste des parcours.
 *
 * ── Trois strates, et le GPS n'est jamais bloquant ──────────────────────────
 *
 * En haut, les trois derniers parcours joués : on retourne au même golf bien
 * plus souvent qu'on n'en découvre un. Viennent ensuite les plus proches, puis
 * l'alphabet. Si la position est refusée — ou si elle tarde — la liste
 * s'affiche quand même, alphabétique : un écran qui attend une autorisation
 * qui ne viendra pas est un écran vide, et le joueur ne saura pas pourquoi.
 *
 * ── Pourquoi le tri vient du serveur ────────────────────────────────────────
 *
 * La liste se charge par tranches de dix. Trier ce qu'on a déjà reçu donnerait
 * un ordre qui change à chaque tranche, et des doublons dès qu'une ligne se
 * glisse entre deux pages. Le client ne fait que concaténer.
 */

const dayLabel = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

export function CoursesList() {
    const { isAdmin } = useAuth();
    /* Un partenaire amene depuis son profil.
     *
     * Il voyage dans l'URL jusqu'a l'ecran de depart, qui le pre-selectionne.
     * Le garder en memoire globale marcherait aussi, mais un lien qui recree
     * l'intention vaut mieux qu'un etat invisible qu'un rafraichissement
     * effacerait. */
    const [params] = useSearchParams();
    const inviteParam = params.get('inviter') ? `?inviter=${params.get('inviter')}` : '';
    const [invitations, setInvitations] = useState<RoundInvitation[]>([]);

    // Les invitations sont lues une fois a l'ouverture : elles se comptent sur
    // les doigts d'une main et n'ont pas besoin d'un flux temps reel.
    useEffect(() => {
        void listRoundInvitations().then(setInvitations).catch(() => { /* sans bruit */ });
    }, []);
    // L'identifiant du parcours qui vient d'etre exporte, pour l'accuse.
    const [exporte, setExporte] = useState<string | null>(null);

    /* Sortir un parcours de l'instance, sous forme de fichier.
     *
     * Un fichier et pas un presse-papier : c'est ce qui se garde, se relit, se
     * corrige dans un editeur de texte et se reimporte. Un presse-papier
     * s'ecrase au premier copier suivant, et ne survit pas au trajet entre
     * deux appareils.
     */
    const exporterParcours = async (c: Course): Promise<void> => {
        setError(null);
        try {
            telechargerDocument(await exportCourse(c.id), c.name);
            setExporte(c.id);
            setTimeout(() => setExporte(null), 2500);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Export impossible');
        }
    };

    const [courses, setCourses] = useState<Course[]>([]);
    const [search, setSearch] = useState('');
    const [pos, setPos] = useState<{ lat: number; lon: number } | null>(null);
    const [posAsked, setPosAsked] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmSuppr, setConfirmSuppr] = useState<Course | null>(null);

    /* La position d'abord, mais sans retenir l'affichage.
     *
     *  On la demande une fois au montage ; qu'elle arrive ou non, la liste
     *  part. Elle déclenche ensuite un rechargement, et l'ordre se resserre
     *  sous les yeux du joueur — ce qui vaut mieux qu'un écran figé six
     *  secondes sur un parking. */
    useEffect(() => {
        void currentPosition().then((p) => { setPos(p); setPosAsked(true); });
    }, []);

    const charger = useCallback(async (
        depuis: number, terme: string, position: { lat: number; lon: number } | null,
    ) => {
        const page = await listCourses({
            search: terme, lat: position?.lat, lon: position?.lon, offset: depuis,
        });
        setCourses((prev) => (depuis === 0 ? page.courses : [...prev, ...page.courses]));
        setOffset(page.nextOffset);
        setHasMore(page.hasMore);
    }, []);

    // Recherche et position repartent de zéro : ce sont deux façons de changer
    // l'ordre, et poursuivre la pagination précédente mélangerait deux tris.
    useEffect(() => {
        if (!posAsked) return;
        let annule = false;
        setLoading(true);
        // Le temps d'arrêt sur la frappe : on tape « saint-cl » lettre à
        // lettre, et huit requêtes pour un mot sont huit fois trop.
        const t = window.setTimeout(() => {
            charger(0, search, pos)
                .catch((e) => { if (!annule) setError(e instanceof ApiError ? e.message : 'Chargement impossible'); })
                .finally(() => { if (!annule) setLoading(false); });
        }, search ? 300 : 0);
        return () => { annule = true; window.clearTimeout(t); };
    }, [search, pos, posAsked, charger]);

    /* Le chargement à l'approche du bas.
     *
     *  Une sentinelle observée plutôt qu'un calcul sur l'événement de
     *  défilement : l'observateur ne se déclenche que lorsqu'elle entre dans
     *  le champ, là où un `scroll` se déclenche des dizaines de fois par
     *  seconde et fait tressauter une liste sur un téléphone modeste. */
    const sentinelle = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const el = sentinelle.current;
        if (!el || !hasMore || loading) return undefined;
        const io = new IntersectionObserver((entries) => {
            if (!entries[0]?.isIntersecting || busy) return;
            setBusy(true);
            charger(offset, search, pos)
                .catch(() => { /* on retentera au prochain passage */ })
                .finally(() => setBusy(false));
        }, { rootMargin: '400px' });
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, loading, busy, offset, search, pos, charger]);

    const supprimer = async (c: Course): Promise<void> => {
        setBusy(true);
        try {
            await deleteCourse(c.id);
            setCourses((prev) => prev.filter((x) => x.id !== c.id));
            setConfirmSuppr(null);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4 py-2">
            {/* Ce qu'on attend de vous, avant tout le reste : une invitation
                qui dort est une partie qui part sans vous. */}
            {invitations.map((inv) => (
                <section key={inv.roundId}
                    className="rounded-box border border-primary bg-base-200 p-4">
                    <div className="flex items-center gap-3">
                        <Avatar name={inv.hostName ?? '?'} avatarUrl={inv.hostAvatarUrl} size={40} />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm">
                                <span className="font-semibold">{inv.hostName ?? 'Un joueur'}</span>
                                {' '}vous invite à jouer
                            </span>
                            <span className="block truncate text-xs opacity-70">
                                {inv.courseName}{inv.city ? ` · ${inv.city}` : ''}
                            </span>
                        </span>
                    </div>
                    <Link to={`/parcours/rejoindre/${inv.roundId}`}
                        className="btn btn-primary btn-block mt-3 h-14 font-bold tap-target">
                        Voir l'invitation
                    </Link>
                </section>
            ))}

            <div className="flex items-baseline justify-between gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight">Parcours</h1>
                <span className="flex items-center gap-1">
                    {/* L'import est discret : on ajoute un golf a la main
                        cent fois pour un carnet transpose d'une instance a
                        l'autre. */}
                    {/* L'historique se cherche apres avoir joue, pas avant :
                        il tient dans une icone a cote des autres. */}
                    <Link to="/parcours/historique" aria-label="Mes parcours joues"
                        className="btn btn-ghost btn-sm btn-square tap-target">
                        <History size={16} />
                    </Link>
                    {isAdmin && (
                        <Link to="/parcours/importer" aria-label="Importer un parcours"
                            className="btn btn-ghost btn-sm btn-square tap-target">
                            {/* Une fleche qui entre dans le bac. */}
                            <Download size={16} />
                        </Link>
                    )}
                    <Link to="/parcours/nouveau" className="btn btn-ghost btn-sm gap-1 tap-target">
                        <Plus size={16} /> Ajouter
                    </Link>
                </span>
            </div>

            {/* La recherche en tête, toujours visible. C'est le geste le plus
                rapide quand on sait déjà où l'on va. */}
            <label className="relative block">
                <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                <input
                    type="search"
                    className="input input-bordered h-14 w-full pl-11 text-base"
                    placeholder="Chercher un golf, une ville"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </label>

            {/* Dire pourquoi l'ordre est ce qu'il est, en une ligne. Sans elle,
                une liste alphabétique passe pour un défaut alors que c'est le
                repli assumé quand la position n'est pas connue. */}
            {posAsked && !pos && (
                <p className="flex items-center gap-1.5 text-xs opacity-70">
                    <MapPin size={13} className="shrink-0" aria-hidden="true" />
                    Position indisponible : les parcours sont classés par ordre alphabétique.
                </p>
            )}

            {error && (
                <div role="alert" className="alert alert-error text-sm">
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setError(null)}>OK</button>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-ring loading-lg text-primary" />
                </div>
            ) : courses.length === 0 ? (
                <div className="rounded-box bg-base-200 p-5">
                    <Flag size={26} className="text-primary" />
                    <p className="mt-2 font-semibold">
                        {search ? 'Aucun golf ne correspond' : 'Aucun parcours pour le moment'}
                    </p>
                    <p className="mt-1 text-sm opacity-70">
                        {search
                            ? 'Essayez le nom de la ville, ou ajoutez ce golf.'
                            : 'Ajoutez le premier : il servira à tous les joueurs.'}
                    </p>
                    <Link to="/parcours/nouveau" className="btn btn-primary mt-4 gap-2 tap-target">
                        <Plus size={18} /> Ajouter un golf
                    </Link>
                </div>
            ) : (
                <ul className="space-y-3">
                    {courses.map((c) => (
                        <li key={c.id} className="rounded-box bg-base-200 p-4">
                            <div className="flex items-start gap-3">
                                <span className="min-w-0 flex-1">
                                    {/* Le nom se replie sur deux lignes plutot
                                        que de se couper : « Golf d... » ne
                                        permet pas de choisir un parcours, et
                                        c'est la seule chose qu'on lit vraiment
                                        dans cette liste. La place verticale ne
                                        manque pas, la place horizontale si. */}
                                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="text-lg font-bold leading-tight">{c.name}</span>
                                        {/* La pastille des parcours déjà joués.
                                            Elle répond à « suis-je déjà venu »,
                                            qu'on se pose avant même de lire les
                                            chiffres. */}
                                        {c.myRounds > 0 && (
                                            <span className="badge badge-primary badge-sm shrink-0 gap-1 font-semibold">
                                                <Flag size={11} aria-hidden="true" /> Joué
                                            </span>
                                        )}
                                    </span>
                                    {/* Jamais la distance au joueur : elle se lisait
                                        comme la longueur du parcours lui-même. Elle
                                        sert déjà, en silence, à trier la liste. */}
                                    <span className="mt-0.5 block text-sm opacity-70">
                                        {[c.city, `${c.holeCount} trous`].filter(Boolean).join(' · ')}
                                    </span>
                                </span>

                                <span className="flex shrink-0 items-center">
                                    {/* L'export est ici et pas dans le carnet :
                                        c'est depuis la liste qu'on decide de
                                        transposer un parcours, sans avoir a
                                        ouvrir dix-huit lignes pour y arriver. */}
                                    {isAdmin && (
                                        <button type="button"
                                            aria-label={`Exporter ${c.name}`}
                                            onClick={() => void exporterParcours(c)}
                                            className="btn btn-ghost btn-sm btn-square tap-target">
                                            {/* Une fleche qui sort du bac :
                                                le parcours quitte l'instance. */}
                                            {exporte === c.id
                                                ? <Check size={16} className="text-success" />
                                                : <Upload size={16} />}
                                        </button>
                                    )}
                                    {c.canEdit && (
                                        <Link to={`/parcours/${c.id}`}
                                            aria-label={`Modifier ${c.name}`}
                                            className="btn btn-ghost btn-sm btn-square tap-target">
                                            <Pencil size={16} />
                                        </Link>
                                    )}
                                    {c.canDelete && (
                                        <button type="button"
                                            aria-label={`Supprimer ${c.name}`}
                                            onClick={() => setConfirmSuppr(c)}
                                            className="btn btn-ghost btn-sm btn-square text-error tap-target">
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </span>
                            </div>

                            {/* Les statistiques du joueur ICI, et non un score
                                général du parcours : ce qui intéresse, c'est
                                ce qu'on y a fait soi-même. */}
                            {c.myRounds > 0 && (
                                <p className="mt-2 border-t border-base-300 pt-2 text-sm">
                                    <span className="font-semibold tabular-nums">
                                        {c.myRounds} partie{c.myRounds > 1 ? 's' : ''}
                                    </span>
                                    {c.myBestStrokes != null && (
                                        <span className="opacity-70">
                                            {' · meilleur score '}
                                            <span className="font-semibold tabular-nums">{c.myBestStrokes}</span>
                                        </span>
                                    )}
                                    {c.myLastPlayedAt && (
                                        <span className="opacity-70">{` · dernière fois le ${dayLabel(c.myLastPlayedAt)}`}</span>
                                    )}
                                </p>
                            )}

                            {/* Jouer prend toute la largeur, et une hauteur de
                                vrai bouton.

                                Serré entre deux liens, il se lisait comme une
                                option de plus alors que c'est la RAISON d'ouvrir
                                cette liste. Sur un téléphone tenu d'une main
                                gantée, un bouton pleine largeur se touche sans
                                viser. */}
                            <Link to={`/parcours/${c.id}/jouer${inviteParam}`}
                                className="btn btn-primary btn-block mt-3 h-14 gap-2 text-base font-bold tap-target">
                                <Play size={20} /> Jouer ce parcours
                            </Link>

                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                <span className="flex flex-wrap items-center gap-1">
                                    {/* Le carnet est ouvert à TOUS en lecture : le
                                        joueur qui s'apprête à jouer a besoin des
                                        pars et des distances, qu'il ait saisi la
                                        fiche ou non. */}
                                    <Link to={`/parcours/${c.id}/carnet`}
                                        className="btn btn-ghost btn-sm gap-2 tap-target">
                                        <BookOpen size={16} /> Carnet
                                    </Link>
                                </span>
                                {c.createdByFirstName && (
                                    <span className="text-xs opacity-60">
                                        Ajouté par {c.createdByFirstName}
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* La sentinelle : invisible, elle déclenche la tranche suivante
                quand elle approche du champ de vision. */}
            <div ref={sentinelle} aria-hidden="true" className="h-1" />
            {busy && hasMore && (
                <div className="flex justify-center py-4">
                    <span className="loading loading-dots loading-md opacity-60" />
                </div>
            )}
            {!loading && !hasMore && courses.length > 10 && (
                <p className="pb-2 text-center text-xs opacity-50">
                    Vous avez vu tous les parcours.
                </p>
            )}

            {confirmSuppr && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Supprimer « {confirmSuppr.name} » ?</h2>
                        <p className="mt-2 text-sm opacity-80">
                            {confirmSuppr.playedByOthers
                                ? "D'autres joueurs y ont déjà joué : leurs parties sont conservées, "
                                  + 'mais le parcours disparaîtra des listes.'
                                : 'Le parcours disparaîtra des listes. Vos parties, s’il y en a, sont conservées.'}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void supprimer(confirmSuppr)}
                                className="btn btn-error btn-block tap-target">Supprimer</button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmSuppr(null)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
